import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  MapPin, 
  CreditCard, 
  Calendar, 
  Clock, 
  MessageCircle,
  Copy,
  Check
} from 'lucide-react';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { useOrders } from '../hooks/useOrders';

const TIMELINE_STEPS = [
  { status: 'pendiente', label: 'Pedido recibido', description: 'Hemos recibido tu pedido' },
  { status: 'confirmado', label: 'Confirmado', description: 'Tu pedido está confirmado' },
  { status: 'en_camino', label: 'En camino', description: 'Tu pedido va en camino' },
  { status: 'entregado', label: 'Entregado', description: 'Pedido entregado' },
];

const OrderDetail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { currentOrder, loading, fetchOrderByCode } = useOrders();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchOrderByCode(code);
  }, [code, fetchOrderByCode]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(currentOrder?.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola, quiero consultar sobre mi pedido #${currentOrder?.code}`
    );
    window.open(`https://wa.me/+56900000000?text=${message}`, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Determinar pasos completados en el timeline
  const getCurrentStepIndex = () => {
    if (!currentOrder) return 0;
    const statusOrder = ['pendiente', 'confirmado', 'en_camino', 'entregado', 'cancelado'];
    if (currentOrder.status === 'cancelado') return -1;
    return statusOrder.indexOf(currentOrder.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentOrder) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-24 px-4 text-center">
        <h1 className="text-2xl font-display font-bold text-[#1B263B] mb-4">
          Pedido no encontrado
        </h1>
        <Link to="/pedidos" className="text-primary font-semibold">
          Ver mis pedidos
        </Link>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();
  const isCancelled = currentOrder.status === 'cancelado';

  return (
    <div className="min-h-screen bg-background pt-16 pb-24">
      {/* Header */}
      <div className="px-4 py-4 border-b border-[#EFEDEA] bg-white">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-xl hover:bg-[#F8F7F5] transition-colors"
          >
            <ArrowLeft size={24} className="text-[#1B263B]" />
          </button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-xl text-[#1B263B]">
              Pedido #{currentOrder.code}
            </h1>
            <p className="text-sm text-[#7A756F]">
              {formatDate(currentOrder.created_at)}
            </p>
          </div>
          <StatusBadge status={currentOrder.status} />
        </div>
      </div>

      <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Timeline */}
        {!isCancelled && (
          <Card>
            <div className="relative">
              {TIMELINE_STEPS.map((step, index) => {
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;

                return (
                  <div key={step.status} className="flex gap-4 relative">
                    {/* Line */}
                    {index < TIMELINE_STEPS.length - 1 && (
                      <div 
                        className={`absolute left-[11px] top-6 w-0.5 h-full ${
                          index < currentStep ? 'bg-success' : 'bg-[#EFEDEA]'
                        }`}
                      />
                    )}
                    
                    {/* Dot */}
                    <div 
                      className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center z-10 ${
                        isCompleted 
                          ? 'bg-success' 
                          : isCurrent 
                            ? 'bg-primary' 
                            : 'bg-[#EFEDEA]'
                      }`}
                    >
                      {isCompleted && (
                        <Check size={14} className="text-white" />
                      )}
                      {isCurrent && !isCompleted && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`pb-6 ${index === TIMELINE_STEPS.length - 1 ? '' : ''}`}>
                      <p className={`font-semibold ${
                        isCompleted || isCurrent ? 'text-[#1B263B]' : 'text-[#9A9590]'
                      }`}>
                        {step.label}
                      </p>
                      <p className="text-sm text-[#7A756F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {isCancelled && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-700 font-medium">
              Este pedido ha sido cancelado
            </p>
          </div>
        )}

        {/* Products */}
        <Card>
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
            <Package size={20} className="text-primary" />
            Productos
          </h2>
          <div className="space-y-4">
            {currentOrder.items?.map((item) => (
              <div 
                key={item.id} 
                className="flex items-center gap-4 py-3 border-b border-[#EFEDEA] last:border-0"
              >
                <div className="w-16 h-16 rounded-lg bg-[#F8F7F5] flex items-center justify-center flex-shrink-0">
                  {item.product_image ? (
                    <img 
                      src={item.product_image} 
                      alt={item.product_name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Package size={24} className="text-[#BCB8B1]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1B263B] truncate">
                    {item.product_name}
                  </p>
                  <p className="text-sm text-[#7A756F]">
                    {item.quantity} x ${item.price?.toLocaleString()}
                  </p>
                </div>
                <p className="font-semibold text-[#1B263B]">
                  ${(item.quantity * item.price)?.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* Delivery Info */}
        <Card>
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-primary" />
            Información de entrega
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-[#7A756F]">Dirección</p>
              <p className="font-medium text-[#1B263B]">{currentOrder.address}</p>
            </div>
            {currentOrder.references && (
              <div>
                <p className="text-sm text-[#7A756F]">Referencias</p>
                <p className="font-medium text-[#1B263B]">{currentOrder.references}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-[#7A756F]">Zona</p>
              <p className="font-medium text-[#1B263B]">{currentOrder.zone_name}</p>
            </div>
            {currentOrder.notes && (
              <div>
                <p className="text-sm text-[#7A756F]">Notas</p>
                <p className="font-medium text-[#1B263B]">{currentOrder.notes}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Payment Info */}
        <Card>
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
            <CreditCard size={20} className="text-primary" />
            Pago
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[#7A756F]">Método</span>
              <span className="font-medium text-[#1B263B] capitalize">
                {currentOrder.payment_method === 'mercadopago' ? 'MercadoPago' : currentOrder.payment_method?.replace('_', ' ')}
              </span>
            </div>
            {currentOrder.payment_method === 'mercadopago' && currentOrder.payment_status && (
              <div className="flex justify-between">
                <span className="text-[#7A756F]">Estado del pago</span>
                <span className={`font-medium capitalize px-2 py-0.5 rounded-full text-xs ${
                  currentOrder.payment_status === 'approved' ? 'bg-green-100 text-green-800' :
                  currentOrder.payment_status === 'rejected' ? 'bg-red-100 text-red-800' :
                  currentOrder.payment_status === 'in_process' ? 'bg-blue-100 text-blue-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {currentOrder.payment_status === 'approved' ? 'Aprobado' :
                   currentOrder.payment_status === 'rejected' ? 'Rechazado' :
                   currentOrder.payment_status === 'in_process' ? 'En proceso' :
                   currentOrder.payment_status === 'pending' ? 'Pendiente' :
                   currentOrder.payment_status}
                </span>
              </div>
            )}
            {currentOrder.payment_id && (
              <div className="flex justify-between">
                <span className="text-[#7A756F]">ID Pago</span>
                <span className="font-mono text-xs text-[#7A756F]">{currentOrder.payment_id}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#7A756F]">Subtotal</span>
              <span className="font-medium text-[#1B263B]">
                ${currentOrder.subtotal?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#7A756F]">Envío</span>
              <span className="font-medium text-[#1B263B]">
                ${currentOrder.shipping_cost?.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-[#EFEDEA] pt-3 flex justify-between items-center">
              <span className="font-display font-bold text-lg text-[#1B263B]">Total</span>
              <span className="font-display font-bold text-2xl text-primary">
                ${currentOrder.total?.toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={handleCopyCode}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? 'Copiado' : 'Copiar código'}
          </Button>
          <Button
            className="flex-1 gap-2 bg-[#38B000] hover:bg-[#2A8500]"
            onClick={handleWhatsApp}
          >
            <MessageCircle size={18} />
            Consultar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
